import {
  Box,
  Flex,
  HStack,
  Icon,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import React, { memo, useMemo, useState } from "react";
import { FiChevronRight } from "react-icons/fi";
import {
  MF_ACCESS_REVIEWS,
  MFCampaign,
  CampaignFilters as CampaignFiltersType,
} from "state/members/accessReviews";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Chip, CountTag, Tabs } from "components/ds";
import CampaignFilters from "./CampaignFilters";
import CampaignMeta from "./components/CampaignMeta";
import ProgressMeter from "./components/ProgressMeter";
import {
  bucketCampaignsByTab,
  campaignStatusPillKind,
  daysUntil,
  isComplete,
  revisionRequestedCount,
  type CampaignTab,
} from "./reviewLogic";
import { campaignProgressLabel } from "./signOffCampaign";
import {
  isEntitlementOnly,
  isOwnerSignOff,
} from "pages/Members/Attestations/signOff";

// Row fade-in-and-rise on load; staggered per index for a cascade.
const rowIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: none; }
`;

// Campaign card — click navigates to the resource drawer (sidepanel). Elevation
// (not a border) defines the card; a revision-flagged campaign gets a warning
// keyline. The meter counts RESOURCE reviews on this surface (accounts live
// inside each review).
const CampaignCard = memo(
  ({ campaign, index }: { campaign: MFCampaign; index: number }) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const { data: campaignDetails } = MF_ACCESS_REVIEWS.useGetCampaignDetails(
      campaign.id,
      undefined,
      { enabled: true },
    );

    const handleClick = () => {
      const params = new URLSearchParams(searchParams);
      const queryString = params.toString();
      navigate(
        `/members/access-reviews/campaigns/${campaign.id}${queryString ? `?${queryString}` : ""}`,
      );
    };

    const reviewsDone = campaignDetails?.stats.completed_reviews ?? 0;
    const reviewsTotal = campaignDetails?.stats.total_reviews ?? 0;
    const reviewsComplete = isComplete(reviewsDone, reviewsTotal);
    const statusKind = campaignStatusPillKind(campaign.status, reviewsComplete);
    const revisionCount = revisionRequestedCount(campaignDetails?.reviews);

    return (
      <Flex
        align="center"
        gap="lg"
        px="xl"
        py="lg"
        bg="bg.container"
        borderRadius="component.2xl"
        boxShadow="elevLow"
        borderWidth="1px"
        borderStyle="solid"
        borderColor={revisionCount > 0 ? "warning.300" : "transparent"}
        cursor="pointer"
        transition="box-shadow 0.12s ease-out"
        _hover={{ boxShadow: "elevHigh" }}
        onClick={handleClick}
        sx={{
          animation: `${rowIn} 0.4s cubic-bezier(0.22, 1, 0.36, 1) both`,
          animationDelay: `${index * 60}ms`,
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        }}
      >
        <Flex direction="column" gap="xs" flex="1" minW={0}>
          <HStack spacing="xs" align="center" minW={0}>
            <Text
              color="text.body.primary"
              fontFamily="heading"
              fontSize="lg"
              fontWeight={500}
              lineHeight="1.2"
              noOfLines={1}
              minW={0}
            >
              {campaign.name}
            </Text>
            {isOwnerSignOff(campaign) && (
              <Chip tone="azurite">Attestation only</Chip>
            )}
            {/* Entitlement-only cycles (PLO-5731) read as ordinary campaigns in
                this list, but a reviewer opening one meets a row per role
                rather than a row per person — say so before they click. */}
            {isEntitlementOnly(campaign) && (
              <Chip tone="warning">Entitlement only</Chip>
            )}
          </HStack>
          {campaign.description && (
            <Text color="text.body.tertiary" fontSize="sm" noOfLines={1}>
              {campaign.description}
            </Text>
          )}
          <Box pt="2xs">
            <CampaignMeta
              campaign={campaign}
              statusKind={statusKind}
              revisionCount={revisionCount}
            />
          </Box>
        </Flex>

        {campaignDetails && (
          <ProgressMeter
            label={campaignProgressLabel(campaign)}
            value={reviewsDone}
            total={reviewsTotal}
            size="md"
            complete={campaign.status === "completed"}
          />
        )}

        <Icon
          as={FiChevronRight}
          boxSize="18px"
          color="text.body.tertiary"
          flexShrink={0}
        />
      </Flex>
    );
  },
);

CampaignCard.displayName = "CampaignCard";

const EmptyTab = ({ label }: { label: string }) => (
  <Flex direction="column" align="center" gap="sm" py="3xl">
    <Text color="text.body.tertiary" fontSize="sm">
      {label}
    </Text>
  </Flex>
);

// The two open-tab groups, in attention order: soonest work first.
const OPEN_GROUPS: { key: MFCampaign["status"]; label: string }[] = [
  { key: "in_progress", label: "In progress" },
  { key: "draft", label: "Not started" },
];

const GroupSection = ({
  label,
  campaigns,
  startIndex,
}: {
  label: string;
  campaigns: MFCampaign[];
  startIndex: number;
}) => (
  <VStack align="stretch" spacing="md">
    <HStack spacing="xs" align="center">
      <Text fontSize="sm" fontWeight={500} color="text.label.default">
        {label}
      </Text>
      <CountTag count={campaigns.length} />
    </HStack>
    {campaigns.map((campaign, i) => (
      <CampaignCard
        key={campaign.id}
        campaign={campaign}
        index={startIndex + i}
      />
    ))}
  </VStack>
);

const CampaignList: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState<CampaignFiltersType | undefined>(
    undefined,
  );
  const [tab, setTab] = useState<CampaignTab>("open");

  // Build sort/filter from URL params (kept from the previous implementation).
  useMemo(() => {
    const sort = searchParams.get("sort") || "inserted_at";
    const newFilters: CampaignFiltersType = {
      sort_by: sort as "due_date" | "inserted_at",
      sort_direction: "desc",
    };
    setFilters(newFilters);
  }, [searchParams]);

  const { data: campaigns, isLoading } =
    MF_ACCESS_REVIEWS.useListCampaigns(filters);

  // Normalise the API response (which may be flat or grouped) into a flat list,
  // then bucket into Open / Completed tabs client-side.
  const flat = useMemo<MFCampaign[]>(() => {
    if (!campaigns) return [];
    if (Array.isArray(campaigns)) return campaigns;
    const grouped = campaigns as {
      overdue?: MFCampaign[];
      in_progress?: MFCampaign[];
      complete?: MFCampaign[];
    };
    return [
      ...(grouped.overdue ?? []),
      ...(grouped.in_progress ?? []),
      ...(grouped.complete ?? []),
    ];
  }, [campaigns]);

  // Reviewer-side progress per campaign — the list payload has none, so this
  // reads it from the same per-campaign details queries the cards already run
  // (PLO-5326). Without it the tab could only see the cycle-wide status, which
  // stays in_progress while the attestor holds a campaign the reviewer has
  // already finished.
  const campaignIds = useMemo(() => flat.map((c) => c.id), [flat]);
  const reviewerProgress = MF_ACCESS_REVIEWS.useCampaignsReviewerProgress(campaignIds);

  const reviewsCompleteById = useMemo(() => {
    const map: Record<string, boolean> = {};
    for (const [id, p] of Object.entries(reviewerProgress)) {
      map[id] = isComplete(p.completed, p.total);
    }
    return map;
  }, [reviewerProgress]);

  const { open, completed } = useMemo(
    () => bucketCampaignsByTab(flat, reviewsCompleteById),
    [flat, reviewsCompleteById],
  );

  // Open tab is grouped (In progress / Not started); soonest-due first within
  // each group. A running index across groups keeps the stagger continuous.
  const openGroups = useMemo(() => {
    let index = 0;
    return OPEN_GROUPS.map(({ key, label }) => {
      const items = open
        .filter((c) => c.status === key)
        .sort((a, b) => daysUntil(a.dueDate) - daysUntil(b.dueDate));
      const startIndex = index;
      index += items.length;
      return { key, label, items, startIndex };
    }).filter((g) => g.items.length > 0);
  }, [open]);

  return (
    <Box width="100%">
      <Box mb="lg">
        <Tabs<CampaignTab>
          tabs={[
            { key: "open", label: "Open", count: open.length },
            { key: "completed", label: "Completed", count: completed.length },
          ]}
          activeKey={tab}
          onChange={setTab}
        />
      </Box>
      <Box pb="lg">
        <CampaignFilters onFilterChange={setFilters} />
      </Box>

      {isLoading ? (
        <Flex justify="center" py="2xl">
          <Spinner size="md" color="neutral.500" />
        </Flex>
      ) : tab === "open" ? (
        openGroups.length === 0 ? (
          <EmptyTab label="No open access reviews right now" />
        ) : (
          <VStack align="stretch" spacing="xl" key="open">
            {openGroups.map((g) => (
              <GroupSection
                key={g.key}
                label={g.label}
                campaigns={g.items}
                startIndex={g.startIndex}
              />
            ))}
          </VStack>
        )
      ) : completed.length === 0 ? (
        <EmptyTab label="No completed access reviews yet" />
      ) : (
        <VStack align="stretch" spacing="md" key="completed">
          {completed.map((campaign, index) => (
            <CampaignCard key={campaign.id} campaign={campaign} index={index} />
          ))}
        </VStack>
      )}
    </Box>
  );
};

export default memo(CampaignList);
